import { BxfNotificationGroup } from '@blk/bxf-react-components';
import {
    BxfNotificationGroupConfig,
    BxfNotificationGroupTypeEnum,
} from '@blk/bxf-web-components';

import { createContext, useContext, useRef } from 'react';
import { NOTIFICATION_GROUP_CONTAINER } from '../constants';

export interface CreateNotificationConfig {
    notification: BxfNotificationGroupConfig;
    callback?: () => void;
}

export const NotificationContext =
    createContext<(notification: CreateNotificationConfig) => void>(undefined);

export const useNotificationContext = () => useContext(NotificationContext);

interface NotificationToastProviderProps {
    children?: JSX.Element;
}

/**
 * NotificationProvider component that provides a context for creating toast notifications.
 *
 * @param {NotificationToastProviderProps} props - The props for the NotificationProvider component.
 * @param {JSX.Element} props.children - The child components to be wrapped by the NotificationProvider.
 *
 * @returns {JSX.Element} The NotificationProvider component.
 *
 * @example
 * ```tsx
 * <NotificationProvider>
 *   <YourComponent />
 * </NotificationProvider>
 * ```
 *
 * @remarks
 * This component uses a context to provide a `createNotification` function that can be used to create toast notifications.
 * It uses a ref to reference the `BxfNotificationGroup` element and calls its `open` method to display notifications.
 *
 * @see NotificationContext
 * @see BxfNotificationGroup
 * @see BxfNotificationGroupTypeEnum
 */
export function NotificationProvider({
    children,
}: NotificationToastProviderProps): JSX.Element {
    const toastNotificationsRef = useRef<HTMLBxfNotificationGroupElement>(null);

    return (
        <NotificationContext.Provider
            value={(config) =>
                toastNotificationsRef.current
                    ?.open(config.notification)
                    .then(config.callback)
            }
        >
            {children}
            <div className={NOTIFICATION_GROUP_CONTAINER}>
                <BxfNotificationGroup
                    ref={toastNotificationsRef}
                    type={BxfNotificationGroupTypeEnum.TOAST}
                />
            </div>
        </NotificationContext.Provider>
    );
}
